import React, { useContext } from 'react';
import { neverop } from 'utils/noop';
import { AppState } from '@landr/auth/services';
import { getIsDAWPlugin, getIsDesktopApp } from 'helpers/executionEnvironment';
import { sendAuthToken, sendLogout } from 'helpers/dawPlugin';
import useAuthenticationEffects, {
    User,
} from './useAuthenticationEffects.hook';

export type AuthenticationContextType = {
    appState?: AppState;
    isAuthenticated: boolean;
    isGuestUser: boolean;
    isLoading: boolean;
    isSignup: boolean;
    user?: User;
    login: (appState?: AppState) => void;
    logout: () => void;
    signup: (appState?: AppState) => void;
    continueOrRedirectToProfileSurvey: () => void;
};

export const defaultAuthenticationContext: AuthenticationContextType = {
    appState: undefined,
    isAuthenticated: false,
    isGuestUser: false,
    isLoading: true,
    isSignup: false,
    user: undefined,
    login: neverop('AuthenticationProvider', 'login'),
    logout: neverop('AuthenticationProvider', 'logout'),
    signup: neverop('AuthenticationProvider', 'signup'),
    continueOrRedirectToProfileSurvey: neverop(
        'AuthenticationProvider',
        'continueOrRedirectToProfileSurvey',
    ),
};

export const AuthenticationContext = React.createContext(
    defaultAuthenticationContext,
);

export const useAuthentication = (): AuthenticationContextType =>
    useContext(AuthenticationContext);

interface AuthenticationProviderProps {
    children: React.ReactNode;
}

export const AuthenticationProvider = ({
    children,
}: AuthenticationProviderProps): JSX.Element => {
    const isDAWPlugin = getIsDAWPlugin();
    const isDesktopApp = getIsDesktopApp();

    const {
        appState,
        isAuthenticated,
        isGuestUser,
        isLoading,
        isSignup,
        user,
        login,
        logout,
        signup,
        continueOrRedirectToProfileSurvey,
    } = useAuthenticationEffects(
        isDAWPlugin,
        isDesktopApp,
        sendAuthToken,
        sendLogout,
    );

    return (
        <AuthenticationContext.Provider
            value={{
                appState,
                isAuthenticated,
                isGuestUser,
                isLoading,
                isSignup,
                user,
                login,
                signup,
                logout,
                continueOrRedirectToProfileSurvey,
            }}
        >
            <>{children}</>
        </AuthenticationContext.Provider>
    );
};
